import { createApp } from 'vue/dist/vue.esm-bundler.js';

// Set Vue to production mode
window.__VUE_OPTIONS_API__ = true;
window.__VUE_PROD_DEVTOOLS__ = false;
window.__VUE_PROD_HYDRATION_MISMATCH_DETAILS__ = false;

// Provider Finder Vue App
document.addEventListener('DOMContentLoaded', () => {
    const providerFinderElements = document.querySelectorAll('.provider-finder-app');

    providerFinderElements.forEach(element => {
        const app = createApp({
            data() {
                return {
                    searchQuery: '',
                    providers: [],
                    filteredProviders: [],
                    loading: false,
                    error: null,
                    userLocation: null,
                    searchRadius: 50, // Default search radius in miles
                    map: null,
                    markers: [],
                    infoWindow: null,
                    selectedProvider: null,
                    totalLocations: 0,
                    googleMapsApiKey: element.dataset.googleMapsKey || 'AIzaSyBEzUi6_qNn64gIv4-heoJ-bpnYK_Bcacw',
                    filterByMapBounds: true, // Filter list by visible map area
                    baseProviders: [], // The base set to filter from (all providers or search results)
                }
            },
            mounted() {
                this.loadProviders();
                this.loadGoogleMaps();
            },
            methods: {
                async loadProviders() {
                    this.loading = true;
                    this.error = null;

                    try {
                        const url = `${window.location.origin}/wp-json/hairmd/v1/providers/search`;
                        const response = await fetch(url);
                        const data = await response.json();

                        if (data.success) {
                            this.providers = data.providers;
                            this.baseProviders = data.providers; // Set base for filtering
                            this.filteredProviders = data.providers;
                            this.totalLocations = data.count;

                            // Update map if it's already initialized
                            if (this.map) {
                                this.updateMap();
                            }
                        } else {
                            throw new Error('Failed to load providers');
                        }
                    } catch (err) {
                        this.error = 'Unable to load providers. Please try again later.';
                    } finally {
                        this.loading = false;
                    }
                },

                async searchProviders() {
                    if (!this.searchQuery.trim()) {
                        this.baseProviders = this.providers;
                        this.filteredProviders = this.providers;
                        this.updateMap();
                        return;
                    }

                    this.loading = true;
                    this.error = null;

                    try {
                        // Try to geocode the search query
                        await this.geocodeSearch(this.searchQuery);

                        let url = `${window.location.origin}/wp-json/hairmd/v1/providers/search?search=${encodeURIComponent(this.searchQuery)}`;

                        if (this.userLocation) {
                            url += `&lat=${this.userLocation.lat}&lng=${this.userLocation.lng}`;
                            url += `&radius=${this.searchRadius}`;
                        }

                        const response = await fetch(url);
                        const data = await response.json();

                        if (data.success) {
                            this.baseProviders = data.providers; // Search results become the base
                            this.filteredProviders = data.providers;
                            this.totalLocations = data.count;
                            this.updateMap();
                        }
                    } catch (err) {
                        this.error = 'Search failed. Please try again.';
                    } finally {
                        this.loading = false;
                    }
                },

                async geocodeSearch(query) {
                    if (!window.google) return;

                    return new Promise((resolve) => {
                        const geocoder = new google.maps.Geocoder();
                        geocoder.geocode({ address: query }, (results, status) => {
                            if (status === 'OK' && results[0]) {
                                this.userLocation = {
                                    lat: results[0].geometry.location.lat(),
                                    lng: results[0].geometry.location.lng(),
                                };
                                resolve();
                            } else {
                                resolve(); // Continue even if geocoding fails
                            }
                        });
                    });
                },

                loadGoogleMaps() {
                    if (window.google && window.google.maps) {
                        this.initMap();
                        return;
                    }

                    // Handle Google Maps auth failure
                    window.gm_authFailure = () => {
                        this.error = 'Map authentication failed. Please contact support.';
                    };

                    const script = document.createElement('script');
                    script.src = `https://maps.googleapis.com/maps/api/js?key=${this.googleMapsApiKey}&callback=Function.prototype`;
                    script.async = true;
                    script.defer = true;
                    script.onload = () => {
                        this.initMap();
                    };
                    script.onerror = () => {
                        this.error = 'Failed to load Google Maps. Please check your internet connection.';
                    };
                    document.head.appendChild(script);
                },

                initMap() {
                    const mapElement = this.$refs.map;
                    if (!mapElement) return;
                    if (!window.google || !window.google.maps) return;

                    try {
                        // Default center (Dallas, TX)
                        const defaultCenter = { lat: 32.7767, lng: -96.7970 };

                        this.map = new google.maps.Map(mapElement, {
                            zoom: 10,
                            center: defaultCenter,
                            mapTypeControl: false,
                            streetViewControl: false,
                            styles: [
                                {
                                    featureType: 'landscape',
                                    elementType: 'geometry',
                                    stylers: [{ color: '#f5f5f5' }]
                                },
                                {
                                    featureType: 'water',
                                    elementType: 'geometry',
                                    stylers: [{ color: '#e0e0e0' }]
                                },
                                {
                                    featureType: 'road',
                                    elementType: 'geometry',
                                    stylers: [{ color: '#ffffff' }]
                                },
                                {
                                    featureType: 'road',
                                    elementType: 'geometry.stroke',
                                    stylers: [{ color: '#d0d0d0' }]
                                },
                                {
                                    featureType: 'road.highway',
                                    elementType: 'geometry',
                                    stylers: [{ color: '#ffffff' }]
                                },
                                {
                                    featureType: 'road.highway',
                                    elementType: 'geometry.stroke',
                                    stylers: [{ color: '#b8b8b8' }]
                                },
                                {
                                    featureType: 'poi',
                                    elementType: 'labels',
                                    stylers: [{ visibility: 'off' }]
                                },
                                {
                                    featureType: 'transit',
                                    stylers: [{ visibility: 'off' }]
                                }
                            ]
                        });

                        // Initialize InfoWindow for tooltips
                        this.infoWindow = new google.maps.InfoWindow({
                            disableAutoPan: true,
                        });

                        // Listen for map idle event (fires after zoom/pan completes)
                        // This updates the provider list to show only those in the visible area
                        this.map.addListener('idle', () => {
                            this.filterProvidersByMapBounds();
                        });

                        // Update map with providers if they're already loaded
                        if (this.providers.length > 0) {
                            this.updateMap();
                        }
                    } catch (error) {
                        this.error = 'Failed to initialize map.';
                    }
                },

                updateMap() {
                    if (!this.map) return;

                    // Clear existing markers
                    this.markers.forEach(marker => marker.setMap(null));
                    this.markers = [];

                    // Add markers for filtered providers
                    const bounds = new google.maps.LatLngBounds();
                    let hasValidLocation = false;
                    let markersAdded = 0;

                    this.filteredProviders.forEach(provider => {
                        if (provider.location && provider.location.latitude && provider.location.longitude) {
                            const lat = parseFloat(provider.location.latitude);
                            const lng = parseFloat(provider.location.longitude);

                            // Validate coordinates
                            if (isNaN(lat) || isNaN(lng)) return;

                            const position = { lat, lng };

                            const marker = new google.maps.Marker({
                                position: position,
                                map: this.map,
                                title: provider.title,
                                icon: {
                                    path: google.maps.SymbolPath.CIRCLE,
                                    scale: 10,
                                    fillColor: '#4A90E2',
                                    fillOpacity: 1,
                                    strokeColor: '#FFFFFF',
                                    strokeWeight: 3
                                }
                            });

                            // Create tooltip content with address and phone
                            const tooltipContent = this.createTooltipContent(provider);

                            // Show tooltip on hover
                            marker.addListener('mouseover', () => {
                                this.infoWindow.setContent(tooltipContent);
                                this.infoWindow.open(this.map, marker);
                            });

                            // Hide tooltip on mouseout
                            marker.addListener('mouseout', () => {
                                this.infoWindow.close();
                            });

                            // Click to navigate to provider profile
                            marker.addListener('click', () => {
                                if (provider.permalink) {
                                    window.location.href = provider.permalink;
                                }
                            });

                            this.markers.push(marker);
                            bounds.extend(position);
                            hasValidLocation = true;
                            markersAdded++;
                        }
                    });

                    // Fit map to markers if we have valid locations
                    if (hasValidLocation) {
                        if (markersAdded === 1) {
                            // For single marker, center and set reasonable zoom instead of fitBounds
                            const singlePosition = this.markers[0].getPosition();
                            this.map.setCenter(singlePosition);
                            this.map.setZoom(12);
                        } else {
                            // For multiple markers, fit bounds with padding
                            this.map.fitBounds(bounds, { padding: 50 });
                        }
                    }
                },

                filterProvidersByMapBounds() {
                    if (!this.map || !this.filterByMapBounds) return;

                    const bounds = this.map.getBounds();
                    if (!bounds) return;

                    // Filter from baseProviders (either all providers or search results)
                    const sourceProviders = this.baseProviders.length > 0 ? this.baseProviders : this.providers;

                    // Filter providers to only those visible in the current map viewport
                    this.filteredProviders = sourceProviders.filter(provider => {
                        if (!provider.location || !provider.location.latitude || !provider.location.longitude) {
                            return false;
                        }

                        const lat = parseFloat(provider.location.latitude);
                        const lng = parseFloat(provider.location.longitude);

                        if (isNaN(lat) || isNaN(lng)) {
                            return false;
                        }

                        const position = new google.maps.LatLng(lat, lng);
                        return bounds.contains(position);
                    });

                    this.totalLocations = this.filteredProviders.length;
                },

                selectProvider(provider) {
                    this.selectedProvider = provider;

                    // Center map on selected provider
                    if (this.map && provider.location && provider.location.latitude && provider.location.longitude) {
                        this.map.setCenter({
                            lat: parseFloat(provider.location.latitude),
                            lng: parseFloat(provider.location.longitude)
                        });
                        this.map.setZoom(14);
                    }

                    // Scroll to provider card
                    const providerCard = this.$refs.providersList?.querySelector(`[data-provider-id="${provider.id}"]`);
                    if (providerCard) {
                        providerCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                    }
                },

                formatDistance(distance) {
                    if (distance === null || distance === undefined) return '';
                    return `${distance} mi`;
                },

                handleSearchInput() {
                    // Debounce search
                    clearTimeout(this.searchTimeout);
                    this.searchTimeout = setTimeout(() => {
                        this.searchProviders();
                    }, 500);
                },

                clearSearch() {
                    this.searchQuery = '';
                    this.userLocation = null;
                    this.baseProviders = this.providers;
                    this.filteredProviders = this.providers;
                    this.totalLocations = this.providers.length;
                    this.updateMap();
                },

                createTooltipContent(provider) {
                    // Build address string
                    let address = '';
                    if (provider.location_string) {
                        address = provider.location_string;
                    } else if (provider.location) {
                        const parts = [];
                        if (provider.location.address) parts.push(provider.location.address);
                        if (provider.location.city) parts.push(provider.location.city);
                        if (provider.location.state) parts.push(provider.location.state);
                        if (provider.location.zip) parts.push(provider.location.zip);
                        address = parts.join(', ');
                    }

                    // Build phone string
                    const phone = provider.phone || '';

                    // Create tooltip HTML with white background
                    return `
                        <div class="provider-tooltip">
                            <div class="provider-tooltip__title">${provider.title}</div>
                            ${address ? `<div class="provider-tooltip__address">${address}</div>` : ''}
                            ${phone ? `<div class="provider-tooltip__phone">${phone}</div>` : ''}
                        </div>
                    `;
                }
            },
            computed: {
                hasResults() {
                    return this.filteredProviders.length > 0;
                }
            }
        });

        app.mount(element);
    });
});
